<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import ContactAPI from 'dashboard/api/contacts';
import Input from 'dashboard/components-next/input/Input.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
});

// { renderedTemplate, templateParams } - templateParams already matches the
// exact shape Campaign#template_params expects (name/category/language/
// namespace/processed_params), the same shape
// Whatsapp::TemplateProcessorService consumes on the backend.
const variables = defineModel('variables', { type: Object, default: null });

const { t } = useI18n();

const parserRef = ref(null);

const sampleContactQuery = ref('');
const sampleContactResults = ref([]);
const sampleContact = ref(null);

let searchTimeout = null;
watch(sampleContactQuery, () => {
  clearTimeout(searchTimeout);
  if (!sampleContactQuery.value.trim()) {
    sampleContactResults.value = [];
    return;
  }
  searchTimeout = setTimeout(async () => {
    const {
      data: { payload },
    } = await ContactAPI.search(sampleContactQuery.value.trim());
    sampleContactResults.value = payload;
  }, 300);
});

const pickSampleContact = contact => {
  sampleContact.value = contact;
  sampleContactResults.value = [];
  sampleContactQuery.value = contact.name || contact.phone_number || '';
};

// Recognizes only the same {{ contact.<field> }} tokens
// Whatsapp::LiquidTemplateProcessorService's ContactDrop supports
// (name/phone_number/email) - a light, clearly-labeled client-side
// approximation, not a real Liquid evaluation. The backend is still the
// only thing that actually renders Liquid at send time.
const CONTACT_TOKEN_REGEX = /{{\s*contact\.(\w+)\s*}}/g;

const sampleContactPreview = computed(() => {
  if (!sampleContact.value) return null;
  const base = parserRef.value?.renderedTemplate || '';
  return base.replace(CONTACT_TOKEN_REGEX, (match, field) => {
    const value = sampleContact.value[field];
    return value || match;
  });
});

const syncVariables = () => {
  if (!parserRef.value) return;
  const { name, category, language, namespace } = props.template;
  variables.value = {
    renderedTemplate: parserRef.value.renderedTemplate,
    isValid: !parserRef.value.v$.$invalid,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: parserRef.value.processedParams,
    },
  };
};

watch(() => parserRef.value?.processedParams, syncVariables, {
  deep: true,
  immediate: true,
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-n-slate-11">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.VARIABLES.STEP_LABEL') }}
    </p>
    <p class="text-xs text-n-slate-11">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.VARIABLES.LIQUID_HINT') }}
    </p>

    <WhatsAppTemplateParser ref="parserRef" :template="template" />

    <div class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-black2">
      <div class="flex items-center justify-between">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.VARIABLES.SAMPLE_CONTACT_LABEL') }}
        </label>
        <Label
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.VARIABLES.APPROXIMATE_BADGE')"
          color="amber"
          compact
        />
      </div>
      <Input
        v-model="sampleContactQuery"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.WIZARD.VARIABLES.SAMPLE_CONTACT_PLACEHOLDER')
        "
      />
      <ul
        v-if="sampleContactResults.length"
        class="flex flex-col gap-1 overflow-y-auto max-h-40"
      >
        <li
          v-for="result in sampleContactResults"
          :key="result.id"
          class="p-2 text-sm rounded-lg cursor-pointer hover:bg-n-slate-3"
          @click="pickSampleContact(result)"
        >
          {{ result.name }}
          <span class="text-n-slate-11">{{ result.phone_number }}</span>
        </li>
      </ul>

      <div v-if="sampleContactPreview" class="text-sm whitespace-pre-wrap">
        <p class="mb-1 text-xs text-n-slate-11">
          {{
            t('CAMPAIGN.WHATSAPP.WIZARD.VARIABLES.PREVIEW_FOR', {
              name: sampleContact.name || sampleContact.phone_number,
            })
          }}
        </p>
        {{ sampleContactPreview }}
      </div>
    </div>
  </div>
</template>
